import { useForm, type SubmitHandler } from "react-hook-form"

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import RHFRichText from "@/components/form-fields/rhf-rich-text"
import type {
    CollectionYard,
    CollectionYardFormInput,
} from "@/types"
import {
    useCreateCollectionYard,
    useUpdateCollectionYard,
} from "@/hooks/api/use-collection-yard"
import RHFTextfield from "@/components/form-fields/rhf-textfield"

const CollectionYardForm = ({
    collectionYard,
    closeCallback,
} : {
    collectionYard?: CollectionYard,
    closeCallback?: () => void,
}) => {

    const formDataId = `collectionYardForm-${collectionYard?.id}-v${APP_VERSION}`

    const initialValues = {
        name: collectionYard ? collectionYard.name : '',
        description: collectionYard ? collectionYard.description : '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        watch,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
    } = useFormStates(handleFormReset)

    const mutationCreate = useCreateCollectionYard({
        setRequestInProgress,
        formDataId,
        onSuccessCallback: closeCallback,
    })

    const mutationUpdate = useUpdateCollectionYard(
        collectionYard?.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
        },
    )

    const onSubmit: SubmitHandler<CollectionYardFormInput> = data => {
        setRequestInProgress(true)
        if (collectionYard?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    const description = watch('description')

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={collectionYard ? 'Upravit obsah' : 'Vytvořit nový sběrný dvůr'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={collectionYard ? 'Uložit' : 'Vytvořit sběrný dvůr'}
            disableSave={!description}
        >
            <RHFTextfield
                name="name"
                control={control}
                label="Název"
                required
            />
            <RHFRichText
                name="description"
                control={control}
                label="Obsah *"
                multipleRows
            />
        </Form>
    )
}

export default CollectionYardForm
